import axios from "axios";

import appConfig from "configs/app.config";
import { REDIRECT_URL_KEY } from "constants/app.constant";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiSignUp } from "services/AuthService";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { initialState, setUser, userLoggedOut } from "store/auth/userSlice";
import useQuery from "./useQuery";

const { apiPrefix } = appConfig;

// Blocked roles that cannot login to the system
const BLOCKED_ROLES = ["ROLE_DEALER", "ROLE_OWNER", "ROLE_CUSTOMER"];

function useAuth() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const query = useQuery();

  const { token, signedIn } = useSelector((state) => state.auth.session);

  const API = axios.create({
    baseURL: apiPrefix,
    withCredentials: true, // Important pour envoyer les cookies HTTP-only
  });

  // Check if user has any blocked roles
  const hasBlockedRole = (userRoles) => {
    if (!userRoles) return false;
    const roles = Array.isArray(userRoles) ? userRoles : [userRoles];
    return roles.some((role) => BLOCKED_ROLES.includes(role));
  };

  const signIn = async (values) => {
    try {
      // const resp = await apiSignIn(values);,
      const { username, password } = values;
      console.log(values);
      const response = await API.post("/login_check", { username, password });
      const { token } = response.data;
      if (token) {
        const { data } = await API.get("/user/infosFromAccessToken", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log(data.user);
        if (data) {
          // Check if user has blocked roles
          const userRoles = data.user?.roles || [];
          if (hasBlockedRole(userRoles)) {
            return {
              status: "failed",
              message:
                "Accès refusé. Votre compte n'a pas les autorisations nécessaires pour se connecter à cette plateforme.",
            };
          }

          dispatch(onSignInSuccess(response.data));
          // Filter out inactive agencies before storing in Redux
          const userData = data.user || {
            avatar: "",
            username: "Anonymous",
            authority: ["USER"],
            email: "",
          };
          // Agency filtering is now done in ModernLayout component
          dispatch(setUser(userData));
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        // Check if user has ROLE_OWNER and redirect to owner dashboard
        const userRoles = data.user?.roles || [];
        const hasOwnerRole = Array.isArray(userRoles)
          ? userRoles.includes("ROLE_OWNER")
          : userRoles === "ROLE_OWNER";

        const targetPath = hasOwnerRole
          ? "/proprietaire"
          : appConfig.authenticatedEntryPath;

        navigate(redirectUrl ? redirectUrl : targetPath);
        return {
          status: "success",
          message: "",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      if (resp.data) {
        const { token } = resp.data;

        if (resp.data.user) {
          // Check if user has blocked roles
          const userRoles = resp.data.user?.roles || [];
          if (hasBlockedRole(userRoles)) {
            return {
              status: "failed",
              message:
                "Accès refusé. Votre compte n'a pas les autorisations nécessaires pour se connecter à cette plateforme.",
            };
          }

          dispatch(onSignInSuccess(token));
          // Filter out inactive agencies before storing in Redux
          const userData = resp.data.user || {
            avatar: "",
            username: "Anonymous",
            authority: ["USER"],
            email: "",
          };
          if (userData.agencies && Array.isArray(userData.agencies)) {
            userData.agencies = userData.agencies.filter(
              (agency) => agency.isActive !== false,
            );
          }
          dispatch(setUser(userData));
        }
        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
        return {
          status: "success",
          message: "",
        };
      }
    } catch (errors) {
      return {
        status: "failed",
        message: errors?.response?.data?.message || errors.toString(),
      };
    }
  };

  const checkSession = () => async (dispatch) => {
    try {
      const { data } = await API.post("/token/refresh");
      const { token } = data;
      if (token) {
        const { data } = await API.get("/user/infosFromAccessToken", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (data) {
          // Check if user has blocked roles
          const userRoles = data.user?.roles || [];
          if (hasBlockedRole(userRoles)) {
            console.warn(
              "Blocked role detected during session check, signing out",
            );
            signOut();
            return;
          }

          dispatch(onSignInSuccess(data));
          const userData = data.user || {
            avatar: "",
            username: "Anonymous",
            authority: ["USER"],
            email: "",
          };
          // Agency filtering is now done in ModernLayout component
          dispatch(setUser(userData));
        }
      }
    } catch (error) {
      console.error("checkSession error:", error);
      signOut();
    }
  };

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());
    dispatch(userLoggedOut());
    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    try {
      // Appel pour invalider le refresh_token sur le serveur
      handleSignOut();
      await API.post("/token/invalidate"); // Utilise POST si la méthode GET pose problème
    } catch (error) {
      console.error("Erreur lors de la déconnexion", error);
    }
  };
  return {
    authenticated: token && signedIn,
    signIn,
    signUp,
    signOut,
    checkSession,
  };
}

export default useAuth;
